import { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import QRCode from 'qrcode'

/**
 * QR code rendu en local.
 *
 * ⭐ Engendré DANS le navigateur, jamais par un service d'images distant. Une
 * fiche client doit s'afficher hors ligne comme le reste de l'application, et
 * envoyer l'identité d'un client à un tiers pour obtenir une image serait un
 * partage de données qu'on n'a pas demandé.
 *
 * ⚠️ Le rendu est ASYNCHRONE : la bibliothèque produit une data-URI. Tant
 * qu'elle n'est pas prête on réserve la place, sinon la fiche sursaute au
 * moment où l'image apparaît.
 */
export function QrCode({
  valeur,
  taille = 128,
  titre,
  bordure = true,
  arrondi = true,
}: {
  valeur: string
  taille?: number
  /** Texte de remplacement — un QR sans description est muet pour un lecteur d'écran. */
  titre: string
  /**
   * ⚠️ Désactivable : le QR de la carte de fidélité se pose sur un fond NOIR,
   * où un filet gris clair ne se voit tout simplement pas — il traçait un
   * rectangle qu'on ne devinait qu'à l'impression, jamais à l'écran.
   */
  bordure?: boolean
  /**
   * ⚠️ Désactivable pour la même raison : un QR aux coins arrondis n'est pas
   * un défaut fonctionnel — les lecteurs le déchiffrent tout aussi bien —
   * mais sur une carte imprimée en petit format, l'arrondi mange les modules
   * d'angle et donne un rendu flou plutôt que net.
   */
  arrondi?: boolean
}): JSX.Element {
  const [image, setImage] = useState<string | null>(null)

  useEffect(() => {
    let vivant = true
    QRCode.toDataURL(valeur, {
      width: taille * 2, // ×2 : net sur les écrans à forte densité et à l'impression.
      margin: 1,
      errorCorrectionLevel: 'M',
      color: { dark: '#000000', light: '#FFFFFF' },
    })
      .then((url) => {
        if (vivant) setImage(url)
      })
      .catch(() => {
        // Un QR absent n'empêche pas de lire la fiche : on ne montre rien
        // plutôt qu'un message d'erreur pour une commodité.
        if (vivant) setImage(null)
      })
    return () => {
      vivant = false
    }
  }, [valeur, taille])

  return (
    <Box
      sx={{
        width: taille,
        height: taille,
        ...(arrondi ? { borderRadius: 2 } : {}),
        ...(bordure ? { border: 1, borderColor: 'divider' } : {}),
        bgcolor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {image && (
        <Box
          component="img"
          src={image}
          alt={titre}
          sx={{ width: '100%', height: '100%', display: 'block' }}
        />
      )}
    </Box>
  )
}
